'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
export function ColorPicker({ label, value, onChange, className = '' }) {
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const buttonRef = useRef(null);
    const presetColors = [
        '#ffffff', '#f8fafc', '#f1f5f9', '#e2e8f0', '#cbd5e1',
        '#94a3b8', '#64748b', '#475569', '#334155', '#1e293b',
        '#0f172a', '#000000', '#fef2f2', '#fee2e2', '#fecaca',
        '#fca5a5', '#f87171', '#ef4444', '#dc2626', '#b91c1c',
        '#991b1b', '#7f1d1d', '#fff7ed', '#ffedd5', '#fed7aa',
        '#fdba74', '#fb923c', '#f97316', '#ea580c', '#c2410c',
        '#9a3412', '#7c2d12', '#fffbeb', '#fef3c7', '#fde68a',
        '#fcd34d', '#fbbf24', '#f59e0b', '#d97706', '#b45309',
        '#92400e', '#78350f', '#f0fdf4', '#dcfce7', '#bbf7d0',
        '#86efac', '#4ade80', '#22c55e', '#16a34a', '#15803d',
        '#166534', '#14532d', '#eff6ff', '#dbeafe', '#bfdbfe',
        '#93c5fd', '#60a5fa', '#3b82f6', '#2563eb', '#1d4ed8',
        '#1e40af', '#1e3a8a', '#faf5ff', '#f3e8ff', '#e9d5ff',
        '#d8b4fe', '#c084fc', '#a855f7', '#9333ea', '#7c3aed',
        '#6b21a8', '#581c87'
    ];
    // Calculate dropdown position
    const handleToggle = () => {
        if (!isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + window.scrollY + 8,
                left: rect.left + window.scrollX
            });
        }
        setIsOpen(!isOpen);
    };
    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (buttonRef.current && !buttonRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);
    return (_jsxs("div", { className: `relative ${className}`, children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2", children: label }), _jsxs("div", { className: "flex items-center space-x-2", children: [_jsx("button", { ref: buttonRef, type: "button", onClick: handleToggle, className: "w-10 h-10 rounded-md border-2 border-gray-300 dark:border-gray-600 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500", style: { backgroundColor: value } }), _jsx("input", { type: "text", value: value, onChange: (e) => onChange(e.target.value), className: "flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500", placeholder: "#ffffff" })] }), isOpen && typeof window !== 'undefined' && createPortal(_jsxs("div", { className: "fixed z-[9999] p-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-xl backdrop-blur-sm", style: {
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                    minWidth: '320px'
                }, children: [_jsx("div", { className: "grid grid-cols-12 gap-1 mb-3", children: presetColors.map((color) => (_jsx("button", { type: "button", onClick: () => {
                                onChange(color);
                                setIsOpen(false);
                            }, className: "w-6 h-6 rounded border border-gray-200 dark:border-gray-600 hover:scale-110 transition-transform", style: { backgroundColor: color }, title: color }, color))) }), _jsxs("div", { className: "flex items-center space-x-2", children: [_jsx("input", { type: "color", value: value, onChange: (e) => onChange(e.target.value), className: "w-8 h-8 border border-gray-300 dark:border-gray-600 rounded cursor-pointer" }), _jsx("span", { className: "text-xs text-gray-500 dark:text-gray-400", children: "Custom color" })] }), _jsx("button", { type: "button", onClick: () => setIsOpen(false), className: "mt-3 w-full px-3 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors", children: "Close" })] }), document.body)] }));
}
